/**
 * "The server did not answer" is not "the server said no".
 *
 * At start-up the app used to treat every failed read as "nobody is signed in" and show the
 * welcome screen, so a database that was slow or down looked like being logged out. These tell
 * the two apart. What each kind of failure really looks like was measured with the real client:
 * scripts/diagnostics/what-the-client-says-when-the-server-is-down.cjs
 */

// What a browser calls a request that got no answer (Chrome, Firefox, Safari, React Native).
const NO_ANSWER = /Failed to fetch|NetworkError|Load failed|Network request failed/i;

// 0: no HTTP answer at all. 5xx: the server, or the gateway in front of it, could not answer.
// 408 and 429: "not now". Every other 4xx is an answer.
const noAnswer = (status) => status === 0 || status === 408 || status === 429 || status >= 500;

/**
 * A database read that failed because the server could not be reached or could not answer.
 * Takes what supabase-js resolves with, `{ error, status }`. "No such row" (PGRST116, a 406) and
 * a refusal (401, 403) are answers, and are not this.
 */
export const couldNotReach = (result) => {
  const { error, status } = result || {};
  if (!error) return false;
  if (typeof status === 'number') return noAnswer(status);
  return NO_ANSWER.test(String(error.message || ''));
};

/**
 * The same for an error from supabase.auth, or one that was thrown: no answer or a 5xx (the
 * library's own "try again" kind, AuthRetryableFetchError, arrives as status 0 or 502 to 504), or
 * an answer it could not read (an error page where JSON should be, which carries no status).
 * "Invalid login credentials" and a refused refresh token are answers.
 */
export const authCouldNotReach = (error) => {
  if (!error) return false;
  if (error.name === 'AuthUnknownError') return true;
  if (typeof error.status === 'number') return noAnswer(error.status);
  return NO_ANSWER.test(String(error.message || ''));
};

export const CANNOT_REACH_MESSAGE =
  "We can't reach the server right now. Nothing is wrong with your details. Wait a moment and try again.";

/** What to show on a sign-in form: the server's own words, unless it never answered. */
export const signInProblem = (error, otherwise) =>
  (authCouldNotReach(error) ? CANNOT_REACH_MESSAGE : error?.message || otherwise);
